import getNumericOption from 'src/internal/date-formatter/getNumericOption';

export type Unit =
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year';

const MS_PER_SECOND = 1e3;
const SECS_PER_MIN = 60;
const SECS_PER_HOUR = SECS_PER_MIN * 60;
const SECS_PER_DAY = SECS_PER_HOUR * 24;
const SECS_PER_WEEK = SECS_PER_DAY * 7;
const SECS_PER_MONTH = SECS_PER_DAY * 30;
const SECS_PER_YEAR = SECS_PER_DAY * 365;

const THRESHOLDS = {
  second: 60, // seconds to minute
  minute: 60, // minutes to hour
  hour: 24, // hour to day
  day: 7, // day to week
  week: 4, // week to month
  month: 12, // month to year
};

export default function selectUnit(
  from: Date | number,
  to: Date | number,
): {value: number; unit: Unit; numeric: 'auto' | 'always'} {
  const secs = (+from - +to) / MS_PER_SECOND;

  function result(
    value: number,
    unit: Unit,
  ): {value: number; unit: Unit; numeric: 'auto' | 'always'} {
    return {value, unit, numeric: getNumericOption(value, unit, secs)};
  }

  if (Math.abs(secs) < THRESHOLDS.second) {
    return result(Math.round(secs), 'second');
  }

  const mins = secs / SECS_PER_MIN;

  if (Math.abs(mins) < THRESHOLDS.minute) {
    return result(Math.round(mins), 'minute');
  }

  const hours = secs / SECS_PER_HOUR;

  if (Math.abs(hours) < THRESHOLDS.hour) {
    return result(Math.round(hours), 'hour');
  }

  const days = secs / SECS_PER_DAY;

  if (Math.abs(days) < THRESHOLDS.day) {
    return result(Math.round(days), 'day');
  }

  const weeks = secs / SECS_PER_WEEK;

  if (Math.abs(weeks) < THRESHOLDS.week) {
    return result(Math.round(weeks), 'week');
  }

  const months = secs / SECS_PER_MONTH;

  if (Math.abs(months) < THRESHOLDS.month) {
    return result(Math.round(months), 'month');
  }

  return result(Math.round(secs / SECS_PER_YEAR), 'year');
}
